/**
 * These helpers are built to add support to the username/password login on the
 * client using a GraphQL mutation to validate credentials.
 *
 * We also calculate the continueUrl depending on different factors:
 *  - It's the first time the user is logged in.
 *  - We use classCodes to join a certain classroom.
 *  - Is the user a teacher.
 *  - It's joining from districts. ...etc.
 *
 * @see https://khanacademy.atlassian.net/wiki/spaces/ENG/pages/658735481/Sign+in+with+username+password
 */
import {t} from "@lingui/core/macro";
import gql from "graphql-tag";

import {
    adaptGraphQLLoginUserData,
    joinClassroomsPostlogin,
    setAnalyticsCookies,
    type LoginTypeData,
} from "@khan/auth-shared";
import {ApolloGqlError, type ApolloGqlClient} from "@khan/data-gql-apollo";
import {gqlOp, type GqlOperation} from "@khan/data-gql-core";
import {doNotTranslateYet} from "@khan/i18n";
import {Errors, KAError} from "@khan/logging";

import type {
    loginWithPasswordAndRecoveryCodeMutationType,
    loginWithPasswordAndTwoFactorCodeMutationType,
    loginWithPasswordMutationType,
    reauthWithPasswordMutationType,
} from "./__graphql-types__/index.ts";

/**
 * Given an error code, return a user friendly error message.
 *
 * @param {string} errorCode The identity/password login error code.
 * @returns {string} The user friendly message.
 */
export const getLoginErrorMessageByCode = (errorCode: string): string => {
    switch (errorCode) {
        case "ALREADY_LOGGED_IN":
            return t`Error logging in. Please try again.`;
        case "NON_UNIQUE_USERNAME":
            return t`Oops! We’re unable to sign you in with that username. Please sign in with your email address and then change your username to fix this issue. If you don’t have an email address, contact support for help.`;
        case "INVALID_CREDENTIALS":
            return t`Your login or password is incorrect.`;
        case "TOO_MANY_REQUESTS":
            return t`Sorry, you've made too many requests.`;
        case "TWO_FACTOR_CODE_INCORRECT":
            return doNotTranslateYet`The code you entered is incorrect. Please try again.`;
        case "TWO_FACTOR_RECOVERY_CODE_INVALID":
            return doNotTranslateYet`The recovery code you entered is incorrect. Please try again.`;
        default:
            return t`Error logging in. Please try again.`;
    }
};

export const PASSWORD_LOGIN_MUTATION: GqlOperation<
    loginWithPasswordMutationType["response"],
    loginWithPasswordMutationType["variables"]
> = gqlOp(gql`
    mutation loginWithPasswordMutation(
        $identifier: String!
        $password: String!
    ) {
        loginWithPassword(identifier: $identifier, password: $password) {
            user {
                id
                kaid
                canAccessDistrictsHomepage
                isTeacher
                hasUnresolvedInvitations
                isInKhanClassroomDistrict
                preferredKaLocale {
                    id
                    kaLocale
                    status
                }
                cleverId
            }
            isFirstLogin
            error {
                code
            }
        }
    }
`);

export const PASSWORD_LOGIN_WITH_TWO_FACTOR_MUTATION: GqlOperation<
    loginWithPasswordAndTwoFactorCodeMutationType["response"],
    loginWithPasswordAndTwoFactorCodeMutationType["variables"]
> = gqlOp(gql`
    mutation loginWithPasswordAndTwoFactorCodeMutation(
        $identifier: String!
        $password: String!
        $twoFactorAuthCode: String!
    ) {
        loginWithPassword(
            identifier: $identifier
            password: $password
            twoFactorAuthCode: $twoFactorAuthCode
        ) {
            user {
                id
                kaid
                canAccessDistrictsHomepage
                isTeacher
                hasUnresolvedInvitations
                isInKhanClassroomDistrict
                preferredKaLocale {
                    id
                    kaLocale
                    status
                }
                cleverId
            }
            isFirstLogin
            error {
                code
            }
        }
    }
`);

export const PASSWORD_LOGIN_WITH_RECOVERY_CODE_MUTATION: GqlOperation<
    loginWithPasswordAndRecoveryCodeMutationType["response"],
    loginWithPasswordAndRecoveryCodeMutationType["variables"]
> = gqlOp(gql`
    mutation loginWithPasswordAndRecoveryCodeMutation(
        $identifier: String!
        $password: String!
        $twoFactorRecoveryCode: String!
    ) {
        loginWithPassword(
            identifier: $identifier
            password: $password
            twoFactorRecoveryCode: $twoFactorRecoveryCode
        ) {
            user {
                id
                kaid
                canAccessDistrictsHomepage
                isTeacher
                hasUnresolvedInvitations
                isInKhanClassroomDistrict
                preferredKaLocale {
                    id
                    kaLocale
                    status
                }
                cleverId
            }
            isFirstLogin
            error {
                code
            }
        }
    }
`);

export interface Options {
    identifier: string;
    password: string;
    signupCodes?: Array<string>;
    twoFactorAuthCode?: string;
    twoFactorRecoveryCode?: string;
}

/**
 * Validates the user credentials against our backend via GraphQL and tries to
 * join a classroom.
 *
 * @param {Options} options The variables that will be sent to the mutation.
 * @param {string} options.identifier The user unique identifier.
 * @param {string} options.password The user password.
 * @param {Array<string>} [options.signupCodes] An optional list of signupCodes
 * passed in the UI. e.g /join/ABC123.
 *
 * @returns {Promise<any>} The mutation result.
 */
export const loginWithPassword = async (
    gqlClient: ApolloGqlClient,
    options: Options,
): Promise<LoginTypeData> => {
    const {signupCodes, ...restOptions} = options;

    try {
        let mutation = PASSWORD_LOGIN_MUTATION;
        if (restOptions.twoFactorAuthCode) {
            mutation = PASSWORD_LOGIN_WITH_TWO_FACTOR_MUTATION;
        } else if (restOptions.twoFactorRecoveryCode) {
            mutation = PASSWORD_LOGIN_WITH_RECOVERY_CODE_MUTATION;
        }

        const result = await gqlClient.mutate(mutation, {
            variables: restOptions,
        });

        if (result.data?.loginWithPassword?.error) {
            // We pass the error code directly, so that code is mapped to an
            // UI readable message.
            throw new KAError(
                result.data.loginWithPassword.error.code,
                Errors.Internal,
            );
        }

        const user = adaptGraphQLLoginUserData(
            result.data?.loginWithPassword?.user,
        );
        const isFirstLogin = !!result.data?.loginWithPassword?.isFirstLogin;

        if (!user) {
            throw new KAError("Failed to login with password", Errors.Internal);
        }

        const userData: LoginTypeData = {
            user,
            isFirstLogin,
        };

        // Only call the joinClassrooms mutation if the user is logged in
        // and is passing signupCodes.
        if (signupCodes && signupCodes.length) {
            userData.joinClassrooms = await joinClassroomsPostlogin(
                gqlClient,
                signupCodes,
            );
        }

        // NOTE: In theory, log in with username/password should be threated
        // as a `returning_login` case , but we are being cautious here, so we
        // will set that value based on the mutation result.
        setAnalyticsCookies(isFirstLogin, user.kaid);

        return userData;
    } catch (error: any) {
        // We handle the exception thrown from within the mutation success
        // handler.
        if (error instanceof KAError && !(error instanceof ApolloGqlError)) {
            throw error;
        }

        // Otherwise we throw a generic error.
        throw new KAError("Failed to login with password", Errors.Internal);
    }
};

/**
 * Allow us to authenticate the current user by entering their password. This is
 * part of the reauth flow where a user needs to verify their identity before
 * linking a third-party account.
 *
 * @see https://khanacademy.atlassian.net/wiki/spaces/ENG/pages/658735481/Sign+in+with+username+password#2.-Re-authentication
 */
export const REAUTH_WITH_PASSWORD_MUTATION: GqlOperation<
    reauthWithPasswordMutationType["response"],
    reauthWithPasswordMutationType["variables"]
> = gqlOp(gql`
    mutation reauthWithPasswordMutation(
        $password: String!
        $twoFactorAuthCode: String
        $twoFactorRecoveryCode: String
    ) {
        reauthWithPassword(
            password: $password
            twoFactorAuthCode: $twoFactorAuthCode
            twoFactorRecoveryCode: $twoFactorRecoveryCode
        ) {
            error {
                code
            }
            noRecoveryCodesRemaining
        }
    }
`);

/**
 * Reauthenticate the current user by sending the password and optional 2FA credentials.
 *
 * @param {string} password The user's password
 * @param {string} twoFactorAuthCode Optional 2FA code from an authenticator app
 * @param {string} twoFactorRecoveryCode Optional recovery code
 * @returns {Promise<{noRecoveryCodesRemaining?: boolean}>} Result with noRecoveryCodesRemaining flag
 */
export const reauthWithPassword = async (
    gqlClient: ApolloGqlClient,
    password: string,
    twoFactorAuthCode?: string,
    twoFactorRecoveryCode?: string,
): Promise<{noRecoveryCodesRemaining?: boolean}> => {
    try {
        const result = await gqlClient.mutate(REAUTH_WITH_PASSWORD_MUTATION, {
            variables: {
                password,
                twoFactorAuthCode: twoFactorAuthCode || undefined,
                twoFactorRecoveryCode: twoFactorRecoveryCode || undefined,
            },
        });

        if (result.data?.reauthWithPassword?.error) {
            // We pass the error code directly, so that code is mapped to an
            // UI readable message.
            throw new KAError(
                result.data.reauthWithPassword.error.code,
                Errors.Internal,
            );
        }

        return {
            noRecoveryCodesRemaining:
                result.data?.reauthWithPassword?.noRecoveryCodesRemaining ??
                false,
        };
    } catch (error: any) {
        // We handle the exception thrown from within the mutation success
        // handler.
        if (error instanceof KAError && !(error instanceof ApolloGqlError)) {
            throw error;
        }

        // Otherwise we throw a generic error.
        throw new KAError("Failed to re-auth", Errors.Internal, {cause: error});
    }
};
